import {
  ValidationError,
  ValidationErrorPeriod,
} from 'components/MenuManagement/MenuEdit/Hours/types';
import { MIDNIGHT } from 'constants/menuManagement';
import { timeToNumber } from './ScheduleSelect/helpers';

// total minutes in a day, used to adjust end times for schedules that roll over past midnight
const MINUTES_IN_DAY = 24 * 60;
const MIDNIGHT_NUMBER = timeToNumber(MIDNIGHT);

export const VALIDATION_MESSAGES = {
  DELIVERY_WITHIN_PICKUP: 'Delivery hours must be within Pickup hours.',
  OVERLAPPING_PERIODS: 'Multiple periods cannot overlap on the same day.',
  SAME_START_END: 'End time cannot be the same as the start time.',
} as const;

type Schedule = {
  dayIndex: string | number;
  startTime: string;
  endTime: string;
  isPeriod?: boolean;
  isDeleted?: boolean;
  uuid?: string;
  tempId?: string;
};

// groups schedules by day and filters out deleted schedules
const groupSchedulesByDay = (schedules: Schedule[]) =>
  schedules
    .filter(schedule => !schedule.isDeleted)
    .reduce(
      (acc, schedule) => ({
        ...acc,
        [schedule.dayIndex]: [...(acc[schedule.dayIndex] || []), schedule],
      }),
      {} as Record<string, Schedule[]>,
    );

const validateDeliverySchedule = (
  deliverySchedule: Schedule,
  pickupRanges: { start: number; end: number }[],
): ValidationErrorPeriod | null => {
  const deliveryStart = timeToNumber(deliverySchedule.startTime);
  const deliveryEnd = timeToNumber(deliverySchedule.endTime);
  const scheduleId = deliverySchedule.uuid || deliverySchedule.tempId || 'temp';

  // skip if it's a closed period (00:00-00:00)
  if (deliveryStart === MIDNIGHT_NUMBER && deliveryEnd === MIDNIGHT_NUMBER) {
    return null;
  }

  // handle rollover hours (when end time is less than start time)
  const isRollover = deliveryEnd < deliveryStart;
  const adjustedDeliveryEnd = isRollover ? deliveryEnd + MINUTES_IN_DAY : deliveryEnd;

  const adjustedPickupRanges = pickupRanges.map(range => ({
    start: range.start,
    end: range.end < range.start ? range.end + MINUTES_IN_DAY : range.end,
  }));

  // check if the delivery period fits entirely within any single pickup period
  // this matches BE behavior where each delivery period must fit within one pickup period
  const isValidPeriod = adjustedPickupRanges.some(
    range => range.start <= deliveryStart && range.end >= adjustedDeliveryEnd,
  );

  if (!isValidPeriod) {
    const invalidTime: 'start' | 'end' | 'both' = 'both';

    return {
      id: scheduleId,
      startTime: deliverySchedule.startTime,
      endTime: deliverySchedule.endTime,
      invalidTime,
    };
  }

  return null;
};

const validateDaySchedules = (
  [dayIndex, dayDeliverySchedules]: [string, Schedule[]],
  pickupByDay: Record<string, Schedule[]>,
): ValidationError | null => {
  const dayPickupSchedules = pickupByDay[dayIndex] || [];

  const pickupRanges = dayPickupSchedules.map(schedule => ({
    start: timeToNumber(schedule.startTime),
    end: timeToNumber(schedule.endTime),
  }));

  const invalidPeriods = dayDeliverySchedules
    .map(schedule => validateDeliverySchedule(schedule, pickupRanges))
    .filter((error): error is NonNullable<typeof error> => error !== null);

  if (invalidPeriods.length > 0) {
    return {
      dayIndex,
      messages: [VALIDATION_MESSAGES.DELIVERY_WITHIN_PICKUP],
      periods: invalidPeriods,
      type: 'delivery',
    };
  }

  return null;
};

const doPeriodsOverlap = (start1: number, end1: number, start2: number, end2: number): boolean => {
  // handle rollover cases (when end time is less than start time)
  const adjustedEnd1 = end1 < start1 ? end1 + MINUTES_IN_DAY : end1;
  const adjustedEnd2 = end2 < start2 ? end2 + MINUTES_IN_DAY : end2;

  return start1 < adjustedEnd2 && start2 < adjustedEnd1;
};

export const validateOverlappingPeriods = (
  schedules: Schedule[],
  type: 'pickup' | 'delivery',
): ValidationError[] => {
  const schedulesByDay = groupSchedulesByDay(schedules);

  return Object.entries(schedulesByDay).reduce<ValidationError[]>(
    (errors, [dayIndex, daySchedules]) => {
      const invalidPeriods = daySchedules.reduce<ValidationErrorPeriod[]>(
        (periods, schedule1, index) => {
          const start1 = timeToNumber(schedule1.startTime);
          const end1 = timeToNumber(schedule1.endTime);

          if (start1 === MIDNIGHT_NUMBER && end1 === MIDNIGHT_NUMBER) return periods;

          const overlappingSchedules = daySchedules.slice(index + 1).filter(schedule2 => {
            const start2 = timeToNumber(schedule2.startTime);
            const end2 = timeToNumber(schedule2.endTime);
            // don't need to check overlap for closed periods
            return (
              (start2 !== MIDNIGHT_NUMBER || end2 !== MIDNIGHT_NUMBER) &&
              doPeriodsOverlap(start1, end1, start2, end2)
            );
          });

          const newPeriods = overlappingSchedules.flatMap(schedule2 =>
            [schedule1, schedule2]
              .filter(schedule => {
                const scheduleId = schedule.uuid || schedule.tempId || 'temp';
                return !periods.some(p => p.id === scheduleId);
              })
              .map(schedule => ({
                id: schedule.uuid || schedule.tempId || 'temp',
                startTime: schedule.startTime,
                endTime: schedule.endTime,
                invalidTime: 'both' as const,
              })),
          );

          return [...periods, ...newPeriods];
        },
        [],
      );

      if (invalidPeriods.length > 0) {
        errors.push({
          dayIndex,
          messages: [VALIDATION_MESSAGES.OVERLAPPING_PERIODS],
          periods: invalidPeriods,
          type,
        });
      }

      return errors;
    },
    [],
  );
};

// validates that delivery hours fall within pickup hours
// returns array of validation errors, empty array if valid
export const validateDeliveryHours = (
  pickupHours: Schedule[],
  deliveryHours: Schedule[],
): ValidationError[] => {
  const pickupByDay = groupSchedulesByDay(pickupHours);
  const deliveryByDay = groupSchedulesByDay(deliveryHours);

  // check if delivery hours are within pickup hours
  const withinPickupErrors = Object.entries(deliveryByDay)
    .map(entry => validateDaySchedules(entry, pickupByDay))
    .filter((error): error is ValidationError => error !== null);

  // check for overlapping periods within delivery hours
  const overlapErrors = validateOverlappingPeriods(deliveryHours, 'delivery');

  // if there are both within pickup and overlap errors, combine them
  const errorsByDay = [...withinPickupErrors, ...overlapErrors].reduce((acc, error) => {
    if (error.type === 'delivery' && acc.has(error.dayIndex)) {
      const existingError = acc.get(error.dayIndex)!;
      acc.set(error.dayIndex, {
        ...existingError,
        messages: [...new Set([...existingError.messages, ...error.messages])],
        periods: [...existingError.periods, ...error.periods],
        type: 'delivery',
      });
    } else {
      acc.set(error.dayIndex, error);
    }
    return acc;
  }, new Map<string | number, ValidationError>());

  const allErrors = Array.from(errorsByDay.values());
  return allErrors;
};

// validates that schedules don't have the same start and end time (except for closed periods)
// returns array of validation errors, empty array if valid
export const validateSameStartEndTime = (
  schedules: Schedule[],
  type: 'pickup' | 'delivery',
): ValidationError[] => {
  const schedulesByDay = groupSchedulesByDay(schedules);

  return Object.entries(schedulesByDay).reduce<ValidationError[]>(
    (errors, [dayIndex, daySchedules]) => {
      const invalidPeriods = daySchedules
        .filter(schedule => {
          const start = timeToNumber(schedule.startTime);
          const end = timeToNumber(schedule.endTime);
          // only flag if start equals end and it's not a closed period (00:00-00:00)
          return start === end && start !== MIDNIGHT_NUMBER;
        })
        .map(schedule => ({
          id: schedule.uuid || schedule.tempId || 'temp',
          startTime: schedule.startTime,
          endTime: schedule.endTime,
          invalidTime: 'both' as const,
        }));

      if (invalidPeriods.length > 0) {
        errors.push({
          dayIndex,
          messages: [VALIDATION_MESSAGES.SAME_START_END],
          periods: invalidPeriods,
          type,
        });
      }

      return errors;
    },
    [],
  );
};

export const getTimeInputError = (
  day: string,
  scheduleId: string,
  isEndTime: boolean,
  findValidationErrors?: (dayIndex: string) => ValidationError[],
): boolean => {
  const errors = findValidationErrors?.(day) ?? [];

  return errors.some(error => {
    const periodError = error.periods.find(period => period.id === scheduleId);
    if (!periodError) return false;

    return (
      periodError.invalidTime === 'both' ||
      (isEndTime && periodError.invalidTime === 'end') ||
      (!isEndTime && periodError.invalidTime === 'start')
    );
  });
};
